import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from 'devextreme-react/button'
import DropDownBox from 'devextreme-react/drop-down-box'
import List from 'devextreme-react/list'
import TextBox from 'devextreme-react/text-box'
import './MultipleSelect.css'

interface MultipleSelectProps {
	data: any
	autoOpen?: boolean
}

const normalizeValue = (v: string | number) => String(v)

export const toMultipleSelectValues = (value: unknown): Array<string | number> => {
	if (Array.isArray(value)) {
		return value
	}
	if (typeof value === 'string' && value.trim()) {
		try {
			const parsed = JSON.parse(value)
			if (Array.isArray(parsed)) {
				return parsed
			}
		} catch {
			// not JSON
		}
		return value
			.split(',')
			.map(part => part.trim())
			.filter(Boolean)
	}
	return []
}

const toKeyArray = toMultipleSelectValues

export const MultipleSelect: React.FC<MultipleSelectProps> = ({ data, autoOpen = true }) => {
	const lookup = data.column?.lookup ?? {}
	const keyExpr = lookup.valueExpr ?? 'value'
	const displayExpr = lookup.displayExpr ?? 'text'
	const dataSource: Array<{ value: string | number; text: string; disabled?: boolean }> =
		data.lookupItems ?? lookup.dataSource ?? []

	const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>(() => toKeyArray(data.value))
	const [pendingKeys, setPendingKeys] = useState<Array<string | number>>(() => toKeyArray(data.value))
	const [isOpen, setIsOpen] = useState(false)
	const listRef = useRef<List>(null)

	// Add popup (new row): the form does not hand the committed value back to this component,
	// so the local selection is kept instead of being reset from a stale data.value
	const isPopupInsert = !!data.row?.isNewRow && data.component?.option?.('editing.mode') === 'popup'
	const popupCommittedRef = useRef(false)

	useEffect(() => {
		if (!autoOpen) return
		const id = requestAnimationFrame(() => setIsOpen(true))
		return () => cancelAnimationFrame(id)
	}, [autoOpen])

	useEffect(() => {
		const keys = toKeyArray(data.value)
		if (isPopupInsert && popupCommittedRef.current && keys.length === 0) return
		setSelectedKeys(keys)
		if (!isOpen) setPendingKeys(keys)
	}, [data.value, isOpen])

	const displayText = useMemo(() => formatMultipleSelectDisplay(selectedKeys, dataSource), [selectedKeys, dataSource])

	const commitSelection = useCallback(
		(keys: Array<string | number>) => {
			popupCommittedRef.current = true
			setSelectedKeys(keys)
			setPendingKeys(keys)
			data.setValue(keys)
		},
		[data]
	)

	const finishGridEdit = useCallback(() => {
		const grid = data.component as { option?: (name: string) => string; closeEditCell?: () => void } | undefined
		const editingMode = grid?.option?.('editing.mode')
		if (editingMode === 'cell' || editingMode === 'batch') {
			setTimeout(() => grid?.closeEditCell?.(), 0)
		}
	}, [data])

	const onSave = useCallback(() => {
		const list = listRef.current?.instance
		const keys = (list?.option('selectedItemKeys') as Array<string | number> | undefined) ?? pendingKeys
		commitSelection(keys)
		setIsOpen(false)
		finishGridEdit()
	}, [commitSelection, pendingKeys, finishGridEdit])

	const onDropDownValueChanged = useCallback(
		(e: { value?: Array<string | number> }) => {
			const next = e.value ?? []
			if (
				isPopupInsert &&
				next.length === selectedKeys.length &&
				next.every(key => selectedKeys.map(normalizeValue).includes(normalizeValue(key)))
			) {
				return
			}
			commitSelection(next)
		},
		[commitSelection, isPopupInsert, selectedKeys]
	)

	const onListSelectionChanged = useCallback((e: { component?: { option: (name: string) => unknown } }) => {
		const keys = (e.component?.option('selectedItemKeys') as Array<string | number>) ?? []
		setPendingKeys(keys)
	}, [])

	const onDropDownOptionChanged = useCallback(
		(e: { name?: string; value?: boolean }) => {
			if (e.name !== 'opened') return
			const opened = !!e.value
			setIsOpen(opened)
			if (opened) {
				setPendingKeys(selectedKeys)
				requestAnimationFrame(() => listRef.current?.instance?.updateDimensions())
			}
		},
		[selectedKeys]
	)

	const onDropdownKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				e.preventDefault()
				onSave()
			}
		},
		[onSave]
	)

	const contentRender = useCallback(
		() => (
			<div className="multiple-select-dropdown-content" onKeyDown={onDropdownKeyDown}>
				<List
					ref={listRef}
					dataSource={dataSource}
					keyExpr={keyExpr}
					displayExpr={displayExpr}
					selectionMode="multiple"
					showSelectionControls={true}
					selectAllMode="allPages"
					selectedItemKeys={pendingKeys}
					searchEnabled={true}
					searchExpr={displayExpr}
					height={240}
					onSelectionChanged={onListSelectionChanged}
				/>
				<div className="multiple-select-dropdown-footer">
					<Button
						text="Save"
						type="default"
						stylingMode="contained"
						width="100%"
						onClick={e => {
							e.event?.stopPropagation()
							onSave()
						}}
					/>
				</div>
			</div>
		),
		[dataSource, keyExpr, displayExpr, pendingKeys, onListSelectionChanged, onDropdownKeyDown, onSave]
	)

	if (!dataSource.length) {
		return <div className="multiple-select-field multiple-select-field--empty">No options</div>
	}

	return (
		<DropDownBox
			className="multiple-select-dropdown"
			dataSource={dataSource}
			value={selectedKeys}
			opened={isOpen}
			placeholder="Select..."
			showClearButton={true}
			dropDownOptions={{
				width: 420,
				maxWidth: 'min(720px, 40vw)',
				height: 320,
			}}
			fieldRender={() => {
				const showChips = isPopupInsert && selectedKeys.length > 0
				return (
					<div style={{ position: 'relative' }}>
						<TextBox
							className="multiple-select-field"
							value={showChips ? '' : displayText}
							readOnly={true}
							placeholder={showChips ? '' : 'Select...'}
						/>
						{showChips && (
							<div
								style={{
									position: 'absolute',
									inset: 0,
									display: 'flex',
									alignItems: 'center',
									padding: '0 9px',
									pointerEvents: 'none',
								}}
							>
								<MultipleSelectCellDisplay values={selectedKeys} lookupList={dataSource} />
							</div>
						)}
					</div>
				)
			}}
			contentRender={contentRender}
			onValueChanged={onDropDownValueChanged}
			onOptionChanged={onDropDownOptionChanged}
		/>
	)
}

const toLookupItems = (
	lookupList: Record<string, string> | Array<{ value: string | number; text: string }> | null | undefined
): Array<{ value: string | number; text: string }> => {
	if (!lookupList) {
		return []
	}
	return Array.isArray(lookupList) ? lookupList : Object.entries(lookupList).map(([value, text]) => ({ value, text }))
}

export const getMultipleSelectLabels = (
	values: Array<string | number> | string | null | undefined,
	lookupList: Record<string, string> | Array<{ value: string | number; text: string }> | null | undefined
): string[] => {
	const normalized = toMultipleSelectValues(values)
	if (!normalized.length) {
		return []
	}
	const items = toLookupItems(lookupList)
	const textByValue = new Map(items.map(item => [normalizeValue(item.value), item.text]))
	return normalized.map(value => textByValue.get(normalizeValue(value)) ?? String(value))
}

export const formatMultipleSelectDisplay = (
	values: Array<string | number> | null | undefined,
	lookupList: Record<string, string> | Array<{ value: string | number; text: string }> | null | undefined
): string => getMultipleSelectLabels(values, lookupList).join(', ')

/** Split Fiber badgeFields values (SQL STRING_AGG with " | "). */
export const splitBadgeFieldValues = (value: unknown, separator = ' | '): string[] => {
	if (value == null || value === '') {
		return []
	}
	return String(value)
		.split(separator)
		.map(part => part.trim())
		.filter(Boolean)
}

const MAX_VISIBLE_CHIPS = 5

interface MultipleSelectCellDisplayProps {
	values: Array<string | number> | string | null | undefined
	lookupList: Record<string, string> | Array<{ value: string | number; text: string }> | null | undefined
	maxVisible?: number
	className?: string
}

export const MultipleSelectCellDisplay: React.FC<MultipleSelectCellDisplayProps> = ({
	values,
	lookupList,
	maxVisible = MAX_VISIBLE_CHIPS,
	className,
}) => {
	const labels = useMemo(() => getMultipleSelectLabels(values, lookupList), [values, lookupList])
	const [hiddenOpen, setHiddenOpen] = useState(false)
	const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({})
	const moreRef = useRef<HTMLSpanElement>(null)

	useEffect(() => {
		if (!hiddenOpen) {
			return
		}
		const close = () => setHiddenOpen(false)
		const onPointerDown = (e: PointerEvent) => {
			const target = e.target as Node
			if (moreRef.current?.contains(target)) {
				return
			}
			if (!(target as Element).closest?.('.multiple-select-hidden-popover')) {
				close()
			}
		}
		document.addEventListener('pointerdown', onPointerDown, true)
		window.addEventListener('scroll', close, true)
		window.addEventListener('resize', close)
		return () => {
			document.removeEventListener('pointerdown', onPointerDown, true)
			window.removeEventListener('scroll', close, true)
			window.removeEventListener('resize', close)
		}
	}, [hiddenOpen])

	if (!labels.length) {
		return null
	}
	const fullText = labels.join(', ')
	const visible = labels.slice(0, maxVisible)
	const hiddenLabels = labels.slice(maxVisible)
	const hiddenCount = hiddenLabels.length

	const toggleHiddenPopover = (e: React.MouseEvent | React.KeyboardEvent) => {
		e.stopPropagation()
		if (hiddenOpen) {
			setHiddenOpen(false)
			return
		}
		const rect = moreRef.current?.getBoundingClientRect()
		if (rect) {
			setPopoverStyle({
				position: 'fixed',
				top: rect.bottom + 4,
				left: rect.left,
				zIndex: 10000,
			})
		}
		setHiddenOpen(true)
	}

	const cellClassName = ['multiple-select-cell', className].filter(Boolean).join(' ')

	return (
		<div className={cellClassName} title={fullText}>
			{visible.map((label, index) => (
				<span key={`${label}-${index}`} className="multiple-select-chip">
					{label}
				</span>
			))}
			{hiddenCount > 0 && (
				<span
					ref={moreRef}
					className="multiple-select-chip multiple-select-chip--more"
					role="button"
					tabIndex={0}
					title={hiddenLabels.join(', ')}
					onClick={toggleHiddenPopover}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							toggleHiddenPopover(e)
						}
					}}
				>
					+{hiddenCount}
				</span>
			)}
			{hiddenOpen &&
				createPortal(
					<div
						className="multiple-select-hidden-popover"
						style={popoverStyle}
						onClick={e => e.stopPropagation()}
					>
						{hiddenLabels.map((label, index) => (
							<span key={`hidden-${label}-${index}`} className="multiple-select-chip">
								{label}
							</span>
						))}
					</div>,
					document.body
				)}
		</div>
	)
}

interface BadgeFieldsCellDisplayProps {
	value: unknown
	maxVisible?: number
}

export const BadgeFieldsCellDisplay: React.FC<BadgeFieldsCellDisplayProps> = ({ value, maxVisible = 2 }) => (
	<MultipleSelectCellDisplay
		values={splitBadgeFieldValues(value)}
		lookupList={null}
		maxVisible={maxVisible}
		className="badge-fields-cell"
	/>
)
